import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
const { FiPlus, FiSearch, FiZap, FiStar, FiChevronDown, FiChevronUp, FiRefreshCcw, FiUnlock, FiSettings, FiLogOut, FiX, FiCopy, FiCode, FiType, FiClock, FiFileText, FiBarChart2, FiCheckSquare, FiWifi, FiWifiOff, FiLayers, FiShield, FiSun, FiMoon, FiGrid, FiList } = FiIcons;
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import SnippetCard from './components/SnippetCard';
import SnippetModal from './components/SnippetModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import CommandPalette from './components/CommandPalette';
import SettingsModal from './components/SettingsModal';
import Toast from './components/Toast';
import ClipboardCaptureUI from './components/ClipboardCaptureUI';
import InsightsModal from './components/InsightsModal';
import DuplicateManagerModal from './components/DuplicateManagerModal';
import BulkActionBar from './components/BulkActionBar';
import TrashModal from './components/TrashModal';
import {
  STORAGE_KEY,
  USAGE_KEY,
  CONFIG_KEY,
  generateId,
  htmlToPlainText,
  sanitizeHtml,
  WORKSPACE_ID,
  APPS_SCRIPT_URL_KEY,
  DIRTY_IDS_KEY,
  LAST_SYNC_AT_KEY,
  TOKEN_VALUES_KEY,
  MAX_RETENTION_MS,
  hashText_,
  titleFromText_,
  replaceSystemTokens,
  extractNonSystemTokens,
  htmlToMarkdown,
  snippetFingerprint,
  CLOUD_SYNC_SAFE_LIMIT
} from './utils/constants';
import { rankSnippets, applyVariables, filterBySection, findExactDuplicateGroups, searchHelp } from './utils/search';
import { loadDirtyIds, persistDirtyIds, mergeRemoteSnapshot } from './utils/sync';

const SESSION_KEY = "sb_session";
const SESSION_TTL_HOURS = 168;
const AUTH_ACTION = "auth";
const SYNC_ACTION = "sync";
const THEME_KEY = "snippet_manager_theme";
const VIEW_KEY = "snippet_manager_view";
const SORT_KEY = "snippet_manager_sort";
const AUTO_SYNC_DEBOUNCE_MS = 2500;
const PULL_THROTTLE_MS = 180000;
const SYNC_REQUEST_TIMEOUT_MS = 45000;
const ACTIVATION_SYNC_THROTTLE_MS = 5000;
const SYNC_BATCH_SIZE = 3;
const SYNC_INTER_BATCH_DELAY_MS = 1250;

const readSyncJson = async (response) => {
  const text = await response.text();
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  try {
    return JSON.parse(text);
  } catch {
    if (/<!doctype|<html/i.test(text)) {
      throw new Error('The sync endpoint returned an HTML page instead of JSON');
    }
    throw new Error('The sync endpoint returned an invalid response');
  }
};

const getNextUpdatedAt = (snippet) => 
  Math.max(Date.now(), Number(snippet?.updatedAt || 0) + 1);

export default function SnippetManager() {
  const [snippets, setSnippets] = useState([]);
  const [usageCounts, setUsageCounts] = useState(() => JSON.parse(localStorage.getItem(USAGE_KEY) || '{}'));
  const [railCollapsed, setRailCollapsed] = useState(() => JSON.parse(localStorage.getItem('rail_collapsed') || 'false'));
  const [syncStatus, setSyncStatus] = useState('idle');
  const [dirtyIds, setDirtyIds] = useState(() => loadDirtyIds(DIRTY_IDS_KEY));
  const [lastSyncAt, setLastSyncAt] = useState(() => Number(localStorage.getItem(LAST_SYNC_AT_KEY) || 0));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('all');
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || 'dark');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_KEY) || 'grid');
  const [sortMode, setSortMode] = useState(() => localStorage.getItem(SORT_KEY) || 'updated');
  const [isOnline, setIsOnline] = useState(() => navigator.onLine !== false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastSyncError, setLastSyncError] = useState('');
  
  const [isAuthed, setIsAuthed] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState(null);
  const [previewSnippet, setPreviewSnippet] = useState(null);
  
  const [tokenModal, setTokenModal] = useState(null);
  const [tokenValues, setTokenValues] = useState({});

  const [deleteId, setDeleteId] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [pendingClipboard, setPendingClipboard] = useState(null);
  const [pendingImport, setPendingImport] = useState(null);
  const [deviceClass, setDeviceClass] = useState('desktop');
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);
  const [isDuplicatesOpen, setIsDuplicatesOpen] = useState(false);
  const [isTrashOpen, setIsTrashOpen] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [paletteResetNonce, setPaletteResetNonce] = useState(0);
  
  // Mobile Specific UI State
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const tabletSearchInputRef = useRef(null);
  const desktopSearchInputRef = useRef(null);
  const importInputRef = useRef(null);

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const isMobileUI = useMemo(() => {
    return window.innerWidth < 1024 || (('ontouchstart' in window) || navigator.maxTouchPoints > 0);
  }, []);

  const [config, setConfig] = useState(() => {
    const saved = localStorage.getItem(CONFIG_KEY);
    const parsed = saved ? JSON.parse(saved) : {};
    return {
      fabPosition: parsed.fabPosition || 'bottom-right',
      autoClipboardCapture: parsed.autoClipboardCapture ?? true,
      captureMode: parsed.captureMode || 'banner',
      mobileClipboardMode: parsed.mobileClipboardMode || 'manual_only',
      rememberVariableValues: parsed.rememberVariableValues ?? true,
      keyboardShortcuts: parsed.keyboardShortcuts ?? true
    };
  });
  
  const [appsScriptUrl, setAppsScriptUrl] = useState(() => localStorage.getItem(APPS_SCRIPT_URL_KEY) || '');

  const cloudBlockedSnippets = useMemo(
    () => snippets.filter(
      snippet => !snippet.isDeleted && String(snippet.content || '').length > CLOUD_SYNC_SAFE_LIMIT
    ),
    [snippets]
  );

  const snippetsRef = useRef(snippets);
  const lastClipboardHashRef = useRef(localStorage.getItem("last_clipboard_hash") || "");
  const lastInternalCopyAtRef = useRef(0);
  const lastInternalCopyHashRef = useRef("");
  const undoSnapshotRef = useRef(null);
  const dirtyIdsRef = useRef(dirtyIds);
  const syncInFlightRef = useRef(false);
  const syncPendingRef = useRef(false);
  const pullInFlightRef = useRef(false);
  const pullPendingRef = useRef(false);
  const pushDirtyRef = useRef(null);
  const autoSyncTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);
  const lastPullAtRef = useRef(0);
  const lastPushHadSnapshotRef = useRef(false);
  const lastActivationSyncAtRef = useRef(0);
  const lastRemoteFingerprintRef = useRef('');
  const clipboardCaptureInFlightRef = useRef(false);

  useEffect(() => { snippetsRef.current = snippets; }, [snippets]);
  useEffect(() => { dirtyIdsRef.current = dirtyIds; }, [dirtyIds]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(VIEW_KEY, viewMode);
    localStorage.setItem(SORT_KEY, sortMode);
  }, [viewMode, sortMode]);

  const toggleTheme = useCallback(() => {
    setTheme(current => current === 'dark' ? 'light' : 'dark');
  }, []);

  useEffect(() => {
    const handleKeydown = (e) => {
      const isK = e.key.toLowerCase() === 'k';
      if ((e.ctrlKey || e.metaKey) && isK) {
        e.preventDefault();
        setPaletteResetNonce(n => n + 1);
        setIsCommandPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeydown, true);
    return () => window.removeEventListener('keydown', handleKeydown, true);
  }, []);

  useEffect(() => {
    const updateViewport = () => {
      if (window.visualViewport) {
        const vv = window.visualViewport;
        const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        setViewportHeight(vv.height);
        setKeyboardInset(overlap);
      } else {
        setViewportHeight(window.innerHeight);
        setKeyboardInset(0);
      }
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    window.addEventListener("orientationchange", updateViewport);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateViewport);
      window.visualViewport.addEventListener("scroll", updateViewport);
    }
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("orientationchange", updateViewport);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateViewport);
        window.visualViewport.removeEventListener("scroll", updateViewport);
      }
    };
  }, []);

  useEffect(() => {
    if (tokenModal || previewSnippet || isModalOpen || isCommandPaletteOpen || isSettingsOpen || isInsightsOpen || isDuplicatesOpen || isTrashOpen || pendingImport) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = original; };
    }
  }, [tokenModal, previewSnippet, isModalOpen, isCommandPaletteOpen, isSettingsOpen, isInsightsOpen, isDuplicatesOpen, isTrashOpen, pendingImport]);

  const saveToStorage = useCallback((newSnippets) => {
    snippetsRef.current = newSnippets;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSnippets));
    setSnippets(newSnippets);
  }, []);

  const showToast = useCallback((message, type = 'success', action = null) => {
    setToast({ message, type, action });
    setTimeout(() => setToast(prev => prev?.message === message ? null : prev), 5000);
  }, []);

  const setDirtyIdsSafely = useCallback((nextIds) => {
    const persisted = persistDirtyIds(DIRTY_IDS_KEY, nextIds);
    dirtyIdsRef.current = persisted;
    setDirtyIds(persisted);
    return persisted;
  }, []);

  const markDirty = useCallback((ids) => {
    const list = Array.isArray(ids) ? ids : [ids];
    return setDirtyIdsSafely([...dirtyIdsRef.current, ...list.filter(Boolean)]);
  }, [setDirtyIdsSafely]);

  const applyRetentionRules = useCallback((items, baseDirtyIds = []) => {
    const now = Date.now();
    const dirty = new Set(baseDirtyIds);
    const next = (items || []).map(snippet => {
      if (snippet.isDeleted || snippet.isPermanent) return snippet;
      const activityAt = Number(snippet.lastUsedAt || snippet.createdAt || 0);
      if (activityAt > 0 && now - activityAt > MAX_RETENTION_MS) {
        dirty.add(snippet.id);
        return {
          ...snippet,
          isDeleted: true,
          updatedAt: Math.max(now, Number(snippet.updatedAt || 0) + 1)
        };
      }
      return snippet;
    });
    return { snippets: next, dirtyIds: [...dirty] };
  }, []);

  const reconcileRemote = useCallback((remoteSnippets) => {
    const merged = mergeRemoteSnapshot(snippetsRef.current, remoteSnippets, dirtyIdsRef.current);
    const retained = applyRetentionRules(merged.snippets, merged.dirtyIds);
    saveToStorage(retained.snippets);
    setDirtyIdsSafely(retained.dirtyIds);
    return retained;
  }, [applyRetentionRules, saveToStorage, setDirtyIdsSafely]);

  const markSyncSuccess = useCallback(() => {
    const syncedAt = Date.now();
    localStorage.setItem(LAST_SYNC_AT_KEY, String(syncedAt));
    setLastSyncAt(syncedAt);
    setLastSyncError('');
    retryAttemptRef.current = 0;
    setRetryCount(0);
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const fetchFromServerOnly = useCallback(async ({ silent = true, force = false } = {}) => {
    if (!appsScriptUrl) return false;
    if (navigator.onLine === false) {
      setSyncStatus('offline');
      return false;
    }

    // Never overlap a read with a write. Queue it instead.
    if (syncInFlightRef.current) {
      pullPendingRef.current = true;
      return true;
    }
    if (pullInFlightRef.current) {
      if (force) pullPendingRef.current = true;
      return true;
    }

    const now = Date.now();
    if (!force && now - lastPullAtRef.current < PULL_THROTTLE_MS) return true;

    pullInFlightRef.current = true;
    if (!silent) setSyncStatus('syncing');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SYNC_REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(
        `${appsScriptUrl}?workspaceId=${encodeURIComponent(WORKSPACE_ID)}&_=${now}`,
        { signal: controller.signal }
      );
      const data = await readSyncJson(response);
      if (!data.ok || !Array.isArray(data.snippets)) {
        throw new Error(data.error || 'Invalid sync response');
      }

      const remoteFingerprint = snippetFingerprint(data.snippets);
      if (remoteFingerprint !== lastRemoteFingerprintRef.current) {
        reconcileRemote(data.snippets);
        lastRemoteFingerprintRef.current = remoteFingerprint;
      }

      lastPullAtRef.current = Date.now();
      markSyncSuccess();
      setSyncStatus(dirtyIdsRef.current.length ? 'syncing' : 'synced');
      return true;
    } catch (error) {
      const offline = navigator.onLine === false;
      const message = error?.name === 'AbortError'
        ? 'Google Sheets sync took too long to respond'
        : (error?.message || 'Unable to refresh from Google Sheets');
      setLastSyncError(message);

      // Background refresh failures should not make the whole app look broken.
      if (offline) setSyncStatus('offline');
      else if (!silent) setSyncStatus('error');
      else setSyncStatus(dirtyIdsRef.current.length ? 'retrying' : 'idle');
      return false;
    } finally {
      clearTimeout(timeoutId);
      pullInFlightRef.current = false;

      if (syncPendingRef.current && dirtyIdsRef.current.length > 0 && navigator.onLine !== false) {
        syncPendingRef.current = false;
        setTimeout(() => pushDirtyRef.current?.({ mode: 'auto' }), 150);
      } else if (pullPendingRef.current && !syncInFlightRef.current && navigator.onLine !== false) {
        pullPendingRef.current = false;
        setTimeout(() => fetchFromServerOnly({ silent: true, force: true }), 150);
      }
    }
  }, [appsScriptUrl, markSyncSuccess, reconcileRemote]);

  const pushDirtyToServer = useCallback(async ({ mode = 'auto' } = {}) => {
    if (!appsScriptUrl) return false;
    if (navigator.onLine === false) {
      setSyncStatus('offline');
      return false;
    }

    // A queued write is not a failed write.
    if (syncInFlightRef.current || pullInFlightRef.current) {
      syncPendingRef.current = true;
      return true;
    }

    if (dirtyIdsRef.current.length === 0) {
      markSyncSuccess();
      setSyncStatus('synced');
      return true;
    }

    syncInFlightRef.current = true;
    lastPushHadSnapshotRef.current = false;
    setSyncStatus('syncing');
    setLastSyncError('');

    try {
      // Google Sheets cannot store more than 50,000 characters in one cell.
      // Remove cloud-unsafe records from the transport queue so one large
      // snippet cannot block all other valid changes from syncing.
      const snippetById = new Map(snippetsRef.current.map(s => [s.id, s]));
      const oversizedDirtyIds = dirtyIdsRef.current.filter(id => {
        const snippet = snippetById.get(id);
        return snippet &&
          !snippet.isDeleted &&
          String(snippet.content || '').length > CLOUD_SYNC_SAFE_LIMIT;
      });

      if (oversizedDirtyIds.length > 0) {
        const oversizedSet = new Set(oversizedDirtyIds);
        setDirtyIdsSafely(dirtyIdsRef.current.filter(id => !oversizedSet.has(id)));
        if (mode === 'manual') {
          showToast(
            `${oversizedDirtyIds.length} snippet${oversizedDirtyIds.length === 1 ? '' : 's'} skipped: too large for Google Sheets. They remain saved locally.`,
            'error'
          );
        }
      }

      while (dirtyIdsRef.current.length > 0) {
        if (navigator.onLine === false) throw new Error('Connection lost while syncing');

        const batchIds = dirtyIdsRef.current.slice(0, SYNC_BATCH_SIZE);
        const byId = new Map(snippetsRef.current.map(s => [s.id, s]));
        const snippetsToPush = batchIds.map(id => byId.get(id)).filter(Boolean);

        // Remove orphaned dirty IDs without discarding real pending changes.
        if (snippetsToPush.length === 0) {
          setDirtyIdsSafely(dirtyIdsRef.current.filter(id => !batchIds.includes(id)));
          continue;
        }

        const sentVersions = new Map(
          snippetsToPush.map(s => [s.id, Number(s.updatedAt || 0)])
        );

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), SYNC_REQUEST_TIMEOUT_MS);

        try {
          const response = await fetch(appsScriptUrl, {
            method: 'POST',
            body: JSON.stringify({
              action: SYNC_ACTION,
              workspaceId: WORKSPACE_ID,
              snippets: snippetsToPush,
              responseMode: 'ack'
            }),
            signal: controller.signal
          });
          const data = await readSyncJson(response);
          if (!data.ok) throw new Error(data.error || 'Sync rejected');

          const currentById = new Map(snippetsRef.current.map(s => [s.id, s]));
          const remainingDirty = dirtyIdsRef.current.filter(id => {
            if (!sentVersions.has(id)) return true;
            const currentVersion = Number(currentById.get(id)?.updatedAt || 0);
            return currentVersion !== sentVersions.get(id);
          });
          setDirtyIdsSafely(remainingDirty);

          // Older deployed endpoints may return the complete library. Reuse it
          // instead of issuing a separate GET after the batch.
          if (Array.isArray(data.snippets)) {
            const remoteFingerprint = snippetFingerprint(data.snippets);
            const merged = mergeRemoteSnapshot(
              snippetsRef.current,
              data.snippets,
              dirtyIdsRef.current
            );
            saveToStorage(merged.snippets);
            setDirtyIdsSafely(merged.dirtyIds);
            lastRemoteFingerprintRef.current = remoteFingerprint;
            lastPushHadSnapshotRef.current = true;
            lastPullAtRef.current = Date.now();
          }

          markSyncSuccess();
          setSyncStatus(dirtyIdsRef.current.length ? 'syncing' : 'synced');
        } finally {
          clearTimeout(timeoutId);
        }

        // Yield briefly between batches so Apps Script/Sheets can complete its
        // previous write cleanly without flooding the endpoint.
        if (dirtyIdsRef.current.length > 0) {
          await new Promise(resolve => setTimeout(resolve, SYNC_INTER_BATCH_DELAY_MS));
        }
      }

      setSyncStatus('synced');
      return true;
    } catch (error) {
      const offline = navigator.onLine === false;
      const message = error?.name === 'AbortError'
        ? 'Google Sheets took too long to save one sync batch'
        : (error?.message || 'Unable to save changes to Google Sheets');
      setLastSyncError(message);
      setSyncStatus(offline ? 'offline' : 'retrying');

      if (!offline && dirtyIdsRef.current.length > 0 && mode !== 'manual') {
        retryAttemptRef.current += 1;
        setRetryCount(retryAttemptRef.current);
        if (retryAttemptRef.current <= 5) {
          const delays = [2000, 5000, 12000, 30000, 60000];
          const delay = delays[retryAttemptRef.current - 1];
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => {
            pushDirtyToServer({ mode: 'auto' });
          }, delay);
        } else {
          setSyncStatus('error');
        }
      }

      if (mode === 'manual') {
        showToast(
          offline
            ? 'Offline. Changes are saved locally.'
            : `Sync paused with ${dirtyIdsRef.current.length} pending: ${message}`,
          'error'
        );
      }
      return false;
    } finally {
      syncInFlightRef.current = false;

      if (syncPendingRef.current && dirtyIdsRef.current.length > 0 && navigator.onLine !== false) {
        syncPendingRef.current = false;
        setTimeout(() => pushDirtyToServer({ mode: 'auto' }), 200);
      } else if (pullPendingRef.current && navigator.onLine !== false) {
        pullPendingRef.current = false;
        setTimeout(() => fetchFromServerOnly({ silent: true, force: true }), 200);
      }
    }
  }, [appsScriptUrl, fetchFromServerOnly, markSyncSuccess, saveToStorage, setDirtyIdsSafely, showToast]);

  useEffect(() => {
    pushDirtyRef.current = pushDirtyToServer;
  }, [pushDirtyToServer]);

  const syncNowInternal = useCallback(async ({ mode = 'manual' } = {}) => {
    if (!appsScriptUrl) {
      if (mode === 'manual') {
        showToast('Configure the Apps Script URL first', 'error');
        setIsSettingsOpen(true);
      }
      return false;
    }
    if (navigator.onLine === false) {
      setSyncStatus('offline');
      if (mode === 'manual') showToast('Offline. Changes remain safely on this device.', 'error');
      return false;
    }

    if (syncInFlightRef.current || pullInFlightRef.current) {
      syncPendingRef.current = dirtyIdsRef.current.length > 0;
      pullPendingRef.current = true;
      if (mode === 'manual') showToast('Sync queued', 'info');
      return true;
    }

    setSyncStatus('syncing');
    setLastSyncError('');
    const hadDirty = dirtyIdsRef.current.length > 0;

    let pushed = true;
    if (hadDirty) pushed = await pushDirtyToServer({ mode });
    if (!pushed) return false;

    // If the deployed endpoint already returned the complete server snapshot,
    // the manual sync is complete without a second network round-trip.
    const needsPull = !hadDirty || !lastPushHadSnapshotRef.current;
    const pulled = needsPull
      ? await fetchFromServerOnly({ silent: mode !== 'manual', force: true })
      : true;

    if (pushed && pulled && dirtyIdsRef.current.length === 0) {
      setSyncStatus('synced');
      if (mode === 'manual') {
        showToast(
          cloudBlockedSnippets.length > 0
            ? `Cloud sync complete. ${cloudBlockedSnippets.length} oversized snippet${cloudBlockedSnippets.length === 1 ? '' : 's'} remain local only.`
            : 'Sync complete',
          cloudBlockedSnippets.length > 0 ? 'info' : 'success'
        );
      }
    }
    return pushed && pulled;
  }, [appsScriptUrl, cloudBlockedSnippets.length, fetchFromServerOnly, pushDirtyToServer, showToast]);

  const scheduleAutoSync = useCallback(() => {
    if (!appsScriptUrl) return;
    if (autoSyncTimerRef.current) clearTimeout(autoSyncTimerRef.current);
    autoSyncTimerRef.current = setTimeout(() => {
      pushDirtyToServer({ mode: 'auto' });
    }, AUTO_SYNC_DEBOUNCE_MS);
  }, [appsScriptUrl, pushDirtyToServer]);

  const commitLocalMutation = useCallback((updated, changedIds, { sync = true } = {}) => {
    saveToStorage(updated);
    markDirty(changedIds);
    if (sync) scheduleAutoSync();
  }, [markDirty, saveToStorage, scheduleAutoSync]);

  const handleTogglePin = useCallback((id) => {
    const current = snippetsRef.current.find(s => s.id === id);
    if (!current) return;
    if (!current.isPinned && snippetsRef.current.filter(s => s.isPinned && !s.isDeleted).length >= 5) {
      showToast('Max 5 pinned', 'error');
      return;
    }
    const updated = snippetsRef.current.map(s =>
      s.id === id ? { ...s, isPinned: !s.isPinned, updatedAt: getNextUpdatedAt(s) } : s
    );
    commitLocalMutation(updated, id);
  }, [commitLocalMutation, showToast]);

  const handleTogglePermanent = useCallback((id) => {
    const updated = snippetsRef.current.map(s =>
      s.id === id ? { ...s, isPermanent: !s.isPermanent, updatedAt: getNextUpdatedAt(s) } : s
    );
    commitLocalMutation(updated, id);
    showToast(updated.find(s => s.id === id)?.isPermanent ? 'Marked Permanent' : 'Permanent removed');
  }, [commitLocalMutation, showToast]);

  useEffect(() => {
    if (!isAuthed) return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      if (Array.isArray(parsed)) {
        const previousDirtyCount = dirtyIdsRef.current.length;
        const retained = applyRetentionRules(parsed, dirtyIdsRef.current);
        saveToStorage(retained.snippets);
        setDirtyIdsSafely(retained.dirtyIds);
        if (retained.dirtyIds.length > previousDirtyCount) scheduleAutoSync();
      }
    } catch (e) { /* keep app usable if local data is malformed */ }
  }, [isAuthed, applyRetentionRules, saveToStorage, setDirtyIdsSafely, scheduleAutoSync]);

  useEffect(() => {
    const session = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    if (session && session.expiresAt > Date.now()) {
      setIsAuthed(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthed) return;
    syncNowInternal({ mode: 'auto' });
  }, [isAuthed, syncNowInternal]);

  useEffect(() => {
    localStorage.setItem(USAGE_KEY, JSON.stringify(usageCounts));
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
    localStorage.setItem('rail_collapsed', JSON.stringify(railCollapsed));
    if (appsScriptUrl) localStorage.setItem(APPS_SCRIPT_URL_KEY, appsScriptUrl);
  }, [usageCounts, config, railCollapsed, appsScriptUrl]);

  const handleUnlock = async (e) => {
    if (e) e.preventDefault();
    if (!passcode || !appsScriptUrl) return;
    setIsAuthLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SYNC_REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(appsScriptUrl, {
        method: 'POST',
        body: JSON.stringify({ action: AUTH_ACTION, passcode }),
        signal: controller.signal
      });
      const data = await readSyncJson(response);
      if (data.ok) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ ...data.user, expiresAt: Date.now() + SESSION_TTL_HOURS * 3600000 }));
        setIsAuthed(true);
        showToast("Welcome back!");
      } else {
        showToast(data.error || "Invalid passcode", "error");
      }
    } catch (error) {
      const message = error?.name === 'AbortError'
        ? 'Login server took too long to respond'
        : (error?.message || 'Unable to reach the login server');
      showToast(message, "error");
    } finally {
      clearTimeout(timeoutId);
      setIsAuthLoading(false);
    }
  };

  const handleUndo = useCallback(() => {
    if (!undoSnapshotRef.current) return;
    const snapshot = undoSnapshotRef.current;
    const snapshotIds = new Set(snapshot.map(s => s.id));
    const now = Date.now();
    const tombstones = snippetsRef.current
      .filter(s => !snapshotIds.has(s.id))
      .map(s => ({ ...s, isDeleted: true, updatedAt: Math.max(now, Number(s.updatedAt || 0) + 1) }));
    const changedIds = tombstones.map(s => s.id);
    commitLocalMutation([...snapshot, ...tombstones], changedIds);
    undoSnapshotRef.current = null;
    showToast('Creation undone');
  }, [commitLocalMutation, showToast]);

  const createSnippetFromData = useCallback((data, hash) => {
    const plainText = data.type === 'html' ? htmlToPlainText(data.content) : data.content;
    const normalized = String(plainText || '').trim();
    if (!normalized) return false;

    const duplicate = snippetsRef.current.some(s => {
      if (s.isDeleted) return false;
      const existing = s.type === 'html' ? htmlToPlainText(s.content) : s.content;
      return String(existing || '').trim() === normalized;
    });
    if (duplicate) {
      if (hash) {
        lastClipboardHashRef.current = hash;
        localStorage.setItem('last_clipboard_hash', hash);
      }
      return false;
    }

    const nowVal = Date.now();
    const newSnippet = {
      id: generateId(),
      title: titleFromText_(normalized),
      type: data.type,
      content: data.type === 'html' ? sanitizeHtml(data.content) : data.content,
      createdAt: nowVal,
      updatedAt: nowVal,
      lastUsedAt: nowVal,
      usageCount: 0,
      isPermanent: false,
      isPinned: false,
      isDeleted: false
    };
    const updated = [newSnippet, ...snippetsRef.current];
    if (String(newSnippet.content || '').length > CLOUD_SYNC_SAFE_LIMIT) {
      saveToStorage(updated);
      showToast(
        `Snippet saved locally only. Reduce it below ${CLOUD_SYNC_SAFE_LIMIT.toLocaleString()} characters to sync with Google Sheets.`,
        'error'
      );
    } else {
      commitLocalMutation(updated, newSnippet.id);
    }
    if (hash) {
      lastClipboardHashRef.current = hash;
      localStorage.setItem('last_clipboard_hash', hash);
    }
    return true;
  }, [commitLocalMutation, saveToStorage, showToast]);

  const detectClipboard = useCallback(async ({ requestedByUser = false } = {}) => {
    if (isModalOpen || editingSnippet || tokenModal || previewSnippet || clipboardCaptureInFlightRef.current) return;
    if (!config.autoClipboardCapture && !requestedByUser) return;
    if (pendingClipboard && !requestedByUser) return;
    
    clipboardCaptureInFlightRef.current = true;
    try {
      let data = null;
      if (navigator.clipboard?.read) {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          if (item.types.includes("text/html")) {
            const blob = await item.getType("text/html");
            data = { type: "html", content: await blob.text() };
            break;
          } else if (item.types.includes("text/plain")) {
            const blob = await item.getType("text/plain");
            data = { type: "plain", content: await blob.text() };
            break;
          }
        }
      } else {
        const text = await navigator.clipboard?.readText();
        if (text) data = { type: "plain", content: text };
      }

      if (!data || !data.content.trim()) return;
      const plainText = data.type === 'html' ? htmlToPlainText(data.content) : data.content;
      const hash = hashText_(plainText);

      if (hash === lastClipboardHashRef.current) return;
      if (hash === lastInternalCopyHashRef.current && (Date.now() - lastInternalCopyAtRef.current < 5000)) return;

      if (deviceClass === 'desktop' && config.captureMode === 'auto_with_undo') {
        undoSnapshotRef.current = [...snippetsRef.current];
        createSnippetFromData(data, hash);
        showToast("Snippet created", "success", { label: "Undo", onClick: handleUndo });
      } else {
        setPendingClipboard({ ...data, hash, preview: plainText.substring(0, 300) });
      }
    } catch (e) { /* ignore access denied */ }
    finally { clipboardCaptureInFlightRef.current = false; }
  }, [config, deviceClass, createSnippetFromData, handleUndo, showToast, pendingClipboard, isModalOpen, editingSnippet, tokenModal, previewSnippet]);

  useEffect(() => {
    if (!isAuthed) return;

    const handleActivation = async () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (!isModalOpen && !editingSnippet && !tokenModal && !previewSnippet) detectClipboard();

      const now = Date.now();
      if (now - lastActivationSyncAtRef.current < ACTIVATION_SYNC_THROTTLE_MS) return;
      lastActivationSyncAtRef.current = now;

      if (dirtyIdsRef.current.length > 0) {
        const pushed = await pushDirtyToServer({ mode: 'auto' });
        if (!pushed) return;
        if (lastPushHadSnapshotRef.current) return;
      }
      await fetchFromServerOnly({ silent: true, force: false });
    };

    window.addEventListener('focus', handleActivation, true);
    document.addEventListener('visibilitychange', handleActivation, true);
    return () => {
      window.removeEventListener('focus', handleActivation, true);
      document.removeEventListener('visibilitychange', handleActivation, true);
    };
  }, [isAuthed, isModalOpen, editingSnippet, tokenModal, previewSnippet, detectClipboard, fetchFromServerOnly, pushDirtyToServer]);

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      if (!isAuthed) return;
      setSyncStatus('syncing');

      if (dirtyIdsRef.current.length > 0) {
        const pushed = await pushDirtyToServer({ mode: 'auto' });
        if (!pushed) return;
        if (lastPushHadSnapshotRef.current) return;
      }
      await fetchFromServerOnly({ silent: true, force: true });
    };

    const handleOffline = () => {
      setIsOnline(false);
      setSyncStatus('offline');
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isAuthed, pushDirtyToServer, fetchFromServerOnly]);

  const executeSnippetCopy = async (snippet, mode = 'plain', varValues = {}) => {
    const systemResolvedContent = replaceSystemTokens(snippet.content);
    if (Object.keys(varValues).length === 0) {
      const plainForCheck = snippet.type === 'html'
        ? htmlToPlainText(systemResolvedContent)
        : systemResolvedContent;
      const tokens = extractNonSystemTokens(plainForCheck);
      if (tokens.length > 0) {
        let remembered = {};
        if (config.rememberVariableValues) {
          try { remembered = JSON.parse(localStorage.getItem(TOKEN_VALUES_KEY) || '{}'); } catch { remembered = {}; }
        }
        setTokenModal({ snippet: { ...snippet, content: systemResolvedContent }, mode });
        setTokenValues(Object.fromEntries(tokens.map(t => [t, remembered[t] || ''])));
        return;
      }
    }

    try {
      let finalContent = systemResolvedContent;
      if (Object.keys(varValues).length > 0) {
        finalContent = applyVariables(finalContent, varValues);
        if (config.rememberVariableValues) {
          let remembered = {};
          try { remembered = JSON.parse(localStorage.getItem(TOKEN_VALUES_KEY) || '{}'); } catch { remembered = {}; }
          localStorage.setItem(TOKEN_VALUES_KEY, JSON.stringify({ ...remembered, ...varValues }));
        }
      }

      const isHtmlSnippet = snippet.type === 'html';
      const isRich = mode === 'html' && isHtmlSnippet;
      let textToCopy;

      if (mode === 'source' && isHtmlSnippet) {
        textToCopy = sanitizeHtml(finalContent);
      } else if (mode === 'markdown') {
        textToCopy = isHtmlSnippet ? htmlToMarkdown(finalContent) : finalContent;
      } else if (isHtmlSnippet) {
        textToCopy = htmlToPlainText(finalContent);
      } else {
        textToCopy = finalContent;
      }

      lastInternalCopyAtRef.current = Date.now();
      lastInternalCopyHashRef.current = hashText_(textToCopy);
      lastClipboardHashRef.current = lastInternalCopyHashRef.current;
      localStorage.setItem('last_clipboard_hash', lastInternalCopyHashRef.current);

      if (isRich && window.ClipboardItem) {
        const htmlBlob = new Blob([sanitizeHtml(finalContent)], { type: 'text/html' });
        const textBlob = new Blob([htmlToPlainText(finalContent)], { type: 'text/plain' });
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);
      } else {
        await navigator.clipboard.writeText(textToCopy);
      }

      setUsageCounts(prev => ({ ...prev, [snippet.id]: Math.max(Number(prev[snippet.id] || 0), Number(snippet.usageCount || 0)) + 1 }));

      if (!snippet.isPermanent) {
        const now = Date.now();
        const updated = snippetsRef.current.map(s =>
          s.id === snippet.id
            ? { ...s, lastUsedAt: now, updatedAt: getNextUpdatedAt(s), usageCount: Number(s.usageCount || 0) + 1 }
            : s
        );
        commitLocalMutation(updated, snippet.id);
      }

      const labels = { html: 'Copied rich text', source: 'Copied HTML source', markdown: 'Copied Markdown', plain: 'Copied' };
      showToast(labels[mode] || 'Copied');
    } catch (err) {
      showToast('Copy failed', 'error');
    }
  };

  useEffect(() => {
    const handleResize = () => setDeviceClass(window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop');
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const exportAllSnippets = useCallback(() => {
    try {
      const data = {
        app: 'snippet-manager',
        version: '2.1.0',
        exportedAt: Date.now(),
        snippets: snippetsRef.current,
        pinnedIds: snippetsRef.current.filter(s => s.isPinned && !s.isDeleted).map(s => s.id),
        usageCountsById: usageCounts
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `snippet-manager-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast('Exported!');
    } catch {
      showToast('Export failed', 'error');
    }
  }, [usageCounts, showToast]);

  const handleImportFile = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      if (parsed?.app !== 'snippet-manager' || !Array.isArray(parsed.snippets)) {
        throw new Error('Invalid backup');
      }
      setPendingImport(parsed);
    } catch {
      showToast('Import failed: invalid backup file', 'error');
    }
  }, [showToast]);

  const applyImport = useCallback((mode) => {
    if (!pendingImport || !Array.isArray(pendingImport.snippets)) return;

    const now = Date.now();
    const existing = snippetsRef.current;
    const normalizedImported = pendingImport.snippets.map(raw => ({
      id: String(raw.id || generateId()),
      title: String(raw.title || 'Untitled Snippet'),
      type: raw.type === 'html' ? 'html' : 'plain',
      content: raw.type === 'html' ? sanitizeHtml(String(raw.content || '')) : String(raw.content || ''),
      createdAt: Number(raw.createdAt || now),
      updatedAt: Number(raw.updatedAt || now),
      lastUsedAt: Number(raw.lastUsedAt || raw.createdAt || now),
      usageCount: Number(raw.usageCount || 0),
      isDeleted: Boolean(raw.isDeleted),
      isPermanent: Boolean(raw.isPermanent),
      isPinned: Boolean(raw.isPinned),
      ownerUserId: String(raw.ownerUserId || '')
    })).filter(s => s.content.trim());

    let next = [];
    const changedIds = new Set();

    if (mode === 'replace') {
      const importedIds = new Set(normalizedImported.map(s => s.id));
      const tombstones = existing
        .filter(s => !importedIds.has(s.id) && !s.isDeleted)
        .map(s => ({
          ...s,
          isDeleted: true,
          updatedAt: Math.max(now, Number(s.updatedAt || 0) + 1)
        }));
      tombstones.forEach(s => changedIds.add(s.id));
      next = [...normalizedImported, ...tombstones];
      normalizedImported.forEach(s => changedIds.add(s.id));
    } else {
      next = [...existing];
      const existingIds = new Set(existing.map(s => s.id));
      const signatures = new Set(existing.filter(s => !s.isDeleted).map(s => `${s.title}\u0000${s.content}`));
      for (const incoming of normalizedImported) {
        const signature = `${incoming.title}\u0000${incoming.content}`;
        if (signatures.has(signature)) continue;
        const id = existingIds.has(incoming.id) ? generateId() : incoming.id;
        const added = { ...incoming, id, updatedAt: Math.max(now, Number(incoming.updatedAt || 0)) };
        next.unshift(added);
        existingIds.add(id);
        signatures.add(signature);
        changedIds.add(id);
      }
    }

    // Preserve at most five pinned snippets to match the quick-access contract.
    let pinsRemaining = 5;
    next = next.map(s => {
      if (s.isDeleted || !s.isPinned) return s;
      if (pinsRemaining > 0) {
        pinsRemaining -= 1;
        return s;
      }
      changedIds.add(s.id);
      return { ...s, isPinned: false, updatedAt: Math.max(now, Number(s.updatedAt || 0) + 1) };
    });

    const importedUsage = pendingImport.usageCountsById || {};
    setUsageCounts(prev => mode === 'replace' ? { ...importedUsage } : { ...prev, ...importedUsage });
    commitLocalMutation(next, [...changedIds]);
    setPendingImport(null);
    showToast(mode === 'replace' ? 'Import replaced local library' : 'Import merged successfully');
  }, [pendingImport, commitLocalMutation, showToast]);

  const toggleSelected = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setIsSelectionMode(false);
  }, []);

  const selectedSnippets = useMemo(
    () => snippets.filter(s => !s.isDeleted && selectedIds.has(s.id)),
    [snippets, selectedIds]
  );

  const exportSelectedSnippets = useCallback(() => {
    if (!selectedSnippets.length) return;
    try {
      const selectedSet = new Set(selectedSnippets.map(s => s.id));
      const usage = Object.fromEntries(Object.entries(usageCounts).filter(([id]) => selectedSet.has(id)));
      const data = {
        app: 'snippet-manager',
        version: '2.2.0',
        exportedAt: Date.now(),
        snippets: selectedSnippets,
        usageCountsById: usage
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `snippet-manager-selected-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast(`Exported ${selectedSnippets.length} snippets`);
    } catch {
      showToast('Selected export failed', 'error');
    }
  }, [selectedSnippets, usageCounts, showToast]);

  const applyBulkPermanent = useCallback((value) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const idSet = new Set(ids);
    const updated = snippetsRef.current.map(s =>
      idSet.has(s.id) && !s.isDeleted
        ? { ...s, isPermanent: value, updatedAt: getNextUpdatedAt(s) }
        : s
    );
    commitLocalMutation(updated, ids);
    showToast(value ? 'Marked selected snippets permanent' : 'Marked selected snippets temporary');
    clearSelection();
  }, [selectedIds, commitLocalMutation, showToast, clearSelection]);

  const applyBulkDelete = useCallback(() => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Move ${ids.length} selected snippet${ids.length === 1 ? '' : 's'} to Trash?`)) return;
    const idSet = new Set(ids);
    const updated = snippetsRef.current.map(s =>
      idSet.has(s.id) && !s.isDeleted
        ? { ...s, isDeleted: true, updatedAt: getNextUpdatedAt(s) }
        : s
    );
    commitLocalMutation(updated, ids);
    showToast(`Deleted ${ids.length} snippets`);
    clearSelection();
  }, [selectedIds, commitLocalMutation, showToast, clearSelection]);

  const applyBulkPin = useCallback(() => {
    const selected = snippetsRef.current.filter(s => selectedIds.has(s.id) && !s.isDeleted && !s.isPinned);
    const currentPinned = snippetsRef.current.filter(s => s.isPinned && !s.isDeleted).length;
    if (currentPinned + selected.length > 5) {
      showToast(`Only ${Math.max(0, 5 - currentPinned)} pin slots are available`, 'error');
      return;
    }
    if (!selected.length) {
      showToast('Selected snippets are already pinned', 'info');
      return;
    }
    const selectedSet = new Set(selected.map(s => s.id));
    const updated = snippetsRef.current.map(s =>
      selectedSet.has(s.id) ? { ...s, isPinned: true, updatedAt: getNextUpdatedAt(s) } : s
    );
    commitLocalMutation(updated, [...selectedSet]);
    showToast(`Pinned ${selected.length} snippets`);
    clearSelection();
  }, [selectedIds, commitLocalMutation, showToast, clearSelection]);

  const applyBulkUnpin = useCallback(() => {
    const ids = snippetsRef.current.filter(s => selectedIds.has(s.id) && s.isPinned && !s.isDeleted).map(s => s.id);
    if (!ids.length) {
      showToast('No selected snippets are pinned', 'info');
      return;
    }
    const idSet = new Set(ids);
    const updated = snippetsRef.current.map(s =>
      idSet.has(s.id) ? { ...s, isPinned: false, updatedAt: getNextUpdatedAt(s) } : s
    );
    commitLocalMutation(updated, ids);
    showToast(`Unpinned ${ids.length} snippets`);
    clearSelection();
  }, [selectedIds, commitLocalMutation, showToast, clearSelection]);

  const duplicateGroups = useMemo(() => findExactDuplicateGroups(snippets), [snippets]);

  const keepBestDuplicate = useCallback((group, keepId) => {
    const deleteIds = group.filter(s => s.id !== keepId).map(s => s.id);
    if (!deleteIds.length) return;
    if (!window.confirm(`Keep the recommended snippet and move ${deleteIds.length} duplicate${deleteIds.length === 1 ? '' : 's'} to Trash?`)) return;
    const deleteSet = new Set(deleteIds);
    const updated = snippetsRef.current.map(s =>
      deleteSet.has(s.id)
        ? { ...s, isDeleted: true, updatedAt: getNextUpdatedAt(s) }
        : s
    );
    commitLocalMutation(updated, deleteIds);
    showToast(`Removed ${deleteIds.length} duplicate${deleteIds.length === 1 ? '' : 's'}`);
  }, [commitLocalMutation, showToast]);

  const restoreSnippet = useCallback((id) => {
    const current = snippetsRef.current.find(s => s.id === id);
    if (!current || !current.isDeleted) return;
    const now = Date.now();
    const updated = snippetsRef.current.map(s =>
      s.id === id
        ? {
            ...s,
            isDeleted: false,
            lastUsedAt: now,
            updatedAt: Math.max(now, Number(s.updatedAt || 0) + 1)
          }
        : s
    );
    commitLocalMutation(updated, id);
    showToast('Snippet restored');
  }, [commitLocalMutation, showToast]);

  const sectionCounts = useMemo(() => {
    const active = snippets.filter(s => !s.isDeleted);
    return {
      all: active.length,
      pinned: active.filter(s => s.isPinned).length,
      text: active.filter(s => !s.type || s.type === 'plain' || s.type === 'text').length,
      templates: active.filter(s => s.type === 'template' || /\{\{\s*[a-zA-Z0-9_]+\s*\}\}/.test(s.content || '')).length,
      html: active.filter(s => s.type === 'html').length,
      recent: active.length,
      popular: active.filter(s => Math.max(Number(usageCounts[s.id] || 0), Number(s.usageCount || 0)) > 0).length,
      permanent: active.filter(s => s.isPermanent).length,
      unused: active.filter(s => Math.max(Number(usageCounts[s.id] || 0), Number(s.usageCount || 0)) === 0).length
    };
  }, [snippets, usageCounts]);

  const rankedResults = useMemo(() => {
    const activeSnippets = snippets.filter(s => !s.isDeleted);
    const sectionFiltered = filterBySection(activeSnippets, activeSection, usageCounts);
    let results = ['recent', 'popular', 'unused'].includes(activeSection) && !searchQuery.trim()
      ? [...sectionFiltered]
      : rankSnippets(sectionFiltered, searchQuery, activeSnippets.filter(s => s.isPinned).map(s => s.id), usageCounts);

    if (searchQuery.trim()) return results;

    if (sortMode === 'updated') {
      results = [...results].sort((a, b) => Number(b.updatedAt || b.createdAt || 0) - Number(a.updatedAt || a.createdAt || 0));
    } else if (sortMode === 'used') {
      results = [...results].sort((a, b) =>
        Math.max(Number(usageCounts[b.id] || 0), Number(b.usageCount || 0)) -
        Math.max(Number(usageCounts[a.id] || 0), Number(a.usageCount || 0))
      );
    } else if (sortMode === 'title') {
      results = [...results].sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    }
    return results;
  }, [snippets, searchQuery, usageCounts, activeSection, sortMode]);

  const commandResults = useMemo(() => {
    const snippetItems = snippets
      .filter(s => !s.isDeleted)
      .map(s => ({
        id: s.id,
        label: s.title || 'Untitled Snippet',
        subtitle: (s.type === 'html' ? htmlToPlainText(s.content) : s.content).slice(0, 120),
        isPinned: !!s.isPinned,
        type: 'snippet',
        action: () => setPreviewSnippet(s)
      }));

    const commands = [
      { id: 'cmd-new', label: 'New snippet', subtitle: 'Create a new snippet', type: 'primary', action: () => setIsModalOpen(true) },
      { id: 'cmd-sync', label: 'Sync now', subtitle: `${dirtyIds.length} pending change${dirtyIds.length === 1 ? '' : 's'}`, type: 'primary', action: () => syncNowInternal({ mode: 'manual' }) },
      { id: 'cmd-settings', label: 'Settings', subtitle: 'Sync, clipboard and app settings', type: 'primary', action: () => setIsSettingsOpen(true) },
      { id: 'cmd-insights', label: 'Library insights', subtitle: 'Usage, popular and unused snippets', type: 'primary', action: () => setIsInsightsOpen(true) },
      { id: 'cmd-duplicates', label: 'Find duplicates', subtitle: `${duplicateGroups.length} exact duplicate group${duplicateGroups.length === 1 ? '' : 's'}`, type: 'primary', action: () => setIsDuplicatesOpen(true) },
      { id: 'cmd-trash', label: 'Trash & recovery', subtitle: `${snippets.filter(s => s.isDeleted).length} deleted snippet${snippets.filter(s => s.isDeleted).length === 1 ? '' : 's'}`, type: 'primary', action: () => setIsTrashOpen(true) },
      { id: 'cmd-select', label: 'Select multiple snippets', subtitle: 'Bulk pin, permanent, export or delete', type: 'primary', action: () => { setIsSelectionMode(true); setSelectedIds(new Set()); } },
      { id: 'cmd-popular', label: 'Show most used', subtitle: 'Open the Popular smart view', type: 'primary', action: () => setActiveSection('popular') },
      { id: 'cmd-unused', label: 'Show never used', subtitle: 'Open the Never Used smart view', type: 'primary', action: () => setActiveSection('unused') },
      { id: 'cmd-export', label: 'Export all snippets', subtitle: 'Download a JSON backup', type: 'primary', action: exportAllSnippets },
      { id: 'cmd-import', label: 'Import snippets', subtitle: 'Merge or replace from JSON backup', type: 'primary', action: () => importInputRef.current?.click() }
    ];
    return [...snippetItems, ...commands];
  }, [snippets, dirtyIds.length, duplicateGroups.length, syncNowInternal, exportAllSnippets]);
  const pinnedSnippetsRail = useMemo(() => {
    const pinned = snippets.filter(s => s.isPinned && !s.isDeleted);
    return pinned.slice(0, 5);
  }, [snippets]);

  useEffect(() => {
    if (!isAuthed || !config.keyboardShortcuts) return;

    const handler = (e) => {
      const target = e.target;
      const isTyping = target instanceof HTMLElement && (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
        target.isContentEditable
      );
      if (isTyping) return;

      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        if (window.innerWidth < 768) {
          setIsMobileSearchOpen(true);
          setTimeout(() => searchInputRef.current?.focus(), 0);
        } else if (window.innerWidth < 1024) {
          tabletSearchInputRef.current?.focus();
        } else {
          desktopSearchInputRef.current?.focus();
        }
        return;
      }

      if (isModalOpen || previewSnippet || tokenModal || isCommandPaletteOpen || isSettingsOpen || isInsightsOpen || isDuplicatesOpen || isTrashOpen) return;

      if (e.key.toLowerCase() === 'n' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setEditingSnippet(null);
        setIsModalOpen(true);
        return;
      }

      if (e.key.toLowerCase() === 's' && e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        syncNowInternal({ mode: 'manual' });
        return;
      }

      if (/^[1-5]$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const index = Number(e.key) - 1;
        const snippet = snippetsRef.current.filter(s => s.isPinned && !s.isDeleted).slice(0, 5)[index];
        if (snippet) {
          e.preventDefault();
          executeSnippetCopy(snippet, 'plain');
        }
        return;
      }

      if (e.key === 'Escape' && isSelectionMode) clearSelection();
    };

    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isAuthed,
    config.keyboardShortcuts,
    isModalOpen,
    previewSnippet,
    tokenModal,
    isCommandPaletteOpen,
    isSettingsOpen,
    isInsightsOpen,
    isDuplicatesOpen,
    isTrashOpen,
    isSelectionMode,
    syncNowInternal,
    clearSelection
  ]);

  const TABS = [
    { id: 'all', label: 'All', icon: FiFileText },
    { id: 'pinned', label: 'Pinned', icon: FiStar },
    { id: 'recent', label: 'Recent', icon: FiClock },
    { id: 'popular', label: 'Popular', icon: FiBarChart2 },
    { id: 'templates', label: 'Templates', icon: FiType },
    { id: 'permanent', label: 'Permanent', icon: FiShield },
    { id: 'unused', label: 'Never Used', icon: FiClock },
    { id: 'text', label: 'Text', icon: FiFileText },
    { id: 'html', label: 'HTML', icon: FiCode }
  ];

  if (!isAuthed) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl shadow-indigo-500/20"><SafeIcon icon={FiZap} className="text-3xl text-white" /></div>
          <h1 className="text-2xl font-bold text-white mb-2 text-center">Snippet Boss</h1>
          <p className="text-gray-400 text-sm">Enter passcode to unlock vault</p>
        </div>
        <form onSubmit={handleUnlock} className="space-y-4">
          <input autoFocus type="password" placeholder="Passcode" className="w-full bg-gray-950 border border-gray-700 rounded-xl px-4 py-4 text-center text-2xl tracking-[0.5em] text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:tracking-normal placeholder:text-sm" value={passcode} onChange={(e) => setPasscode(e.target.value)} />
          <button type="submit" disabled={isAuthLoading} className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all">
            {isAuthLoading ? <SafeIcon icon={FiRefreshCcw} className="animate-spin" /> : <SafeIcon icon={FiUnlock} />} UNLOCK
          </button>
        </form>
        <button onClick={() => setIsSettingsOpen(true)} className="w-full mt-6 text-xs text-gray-500 hover:text-indigo-400 flex items-center justify-center gap-1.5 transition-colors"><SafeIcon icon={FiSettings} /> Configure Sync URL</button>
        {isSettingsOpen && <SettingsModal onClose={() => setIsSettingsOpen(false)} config={config} setConfig={setConfig} appsScriptUrl={appsScriptUrl} setAppsScriptUrl={setAppsScriptUrl} onSyncNow={syncNowInternal} syncStatus={syncStatus} dirtyCount={dirtyIds.length} lastSyncAt={lastSyncAt} isOnline={isOnline} retryCount={retryCount} lastSyncError={lastSyncError} blockedCount={cloudBlockedSnippets.length} onExport={exportAllSnippets} onImport={() => importInputRef.current?.click()} onInsights={() => { setIsSettingsOpen(false); setIsInsightsOpen(true); }} onDuplicates={() => { setIsSettingsOpen(false); setIsDuplicatesOpen(true); }} onTrash={() => { setIsSettingsOpen(false); setIsTrashOpen(true); }} theme={theme} onToggleTheme={toggleTheme} />}
      </motion.div>
    </div>
  );

  return (
    <div className="app-native-root selection:bg-indigo-500/30 pb-24 md:pb-0">
      <input ref={importInputRef} type="file" accept=".json,application/json" className="hidden" onChange={handleImportFile} />
      
      {/* HEADER */}
      <header className="premium-header native-safe-top sticky top-0 z-40 backdrop-blur-xl border-b">
        
        {/* Desktop Header */}
        <div className="desktop-native-header hidden lg:flex snippet-shell h-16 items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600 rounded-lg shrink-0"><SafeIcon icon={FiZap} className="text-white" /></div>
            <div>
              <h1 className="text-sm font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">Snippet Boss</h1>
              <div className="flex items-center gap-1.5">
                <SafeIcon icon={isOnline ? FiWifi : FiWifiOff} className={`text-[10px] ${!isOnline ? 'text-gray-500' : syncStatus === 'error' ? 'text-red-400' : syncStatus === 'retrying' ? 'text-amber-400' : syncStatus === 'syncing' ? 'text-indigo-400' : 'text-emerald-400'}`} />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                  {!isOnline ? 'offline' : syncStatus}{dirtyIds.length ? ` · ${dirtyIds.length} pending` : ''}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex-1 min-w-[200px] relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input ref={desktopSearchInputRef} type="text" title={`Filters: ${searchHelp.join(' · ')}`} placeholder="Search…  try is:permanent or used:never" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="header-search w-full rounded-xl pl-10 pr-14 py-2 text-sm outline-none transition-all" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-bold theme-muted border theme-border rounded-md px-1.5 py-0.5">⌘K</span>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} className="top-action min-h-[40px] px-3 rounded-xl transition-all"><SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} /></button>
            <button onClick={() => setIsInsightsOpen(true)} title="Library insights" className="top-action min-h-[40px] px-3 rounded-xl transition-all"><SafeIcon icon={FiBarChart2} /></button>
            <button onClick={() => setIsDuplicatesOpen(true)} title="Duplicate manager" className="top-action min-h-[40px] px-3 rounded-xl transition-all"><SafeIcon icon={FiLayers} /></button>
            <button onClick={() => { setIsSelectionMode(v => !v); setSelectedIds(new Set()); }} title="Bulk select" className={`min-h-[40px] px-3 rounded-xl border transition-all ${isSelectionMode ? 'bg-indigo-600/20 border-indigo-500 text-indigo-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'}`}><SafeIcon icon={FiCheckSquare} /></button>
            <button onClick={() => syncNowInternal({ mode: 'manual' })} className="top-action min-h-[40px] px-3 rounded-xl flex items-center gap-2 transition-all">
              <SafeIcon icon={FiRefreshCcw} className={syncStatus === 'syncing' ? 'animate-spin' : ''} />
              <span className="text-xs font-bold uppercase tracking-wider">Sync</span>
            </button>
            <button onClick={() => setIsModalOpen(true)} className="min-h-[40px] bg-indigo-600 hover:bg-indigo-700 text-white px-4 rounded-xl font-medium flex items-center gap-2 transition-colors">
              <SafeIcon icon={FiPlus} />
              <span>New</span>
            </button>
            <button onClick={() => { localStorage.removeItem(SESSION_KEY); setIsAuthed(false); }} className="min-h-[40px] px-3 text-gray-500 hover:text-red-400 transition-colors"><SafeIcon icon={FiLogOut} className="text-lg" /></button>
          </div>
        </div>

        {/* Tablet / iPad Header */}
        <div className="tablet-native-header hidden md:flex lg:hidden snippet-shell h-[68px] items-center gap-3">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="p-2 bg-indigo-600 rounded-xl shadow-lg shadow-indigo-500/20"><SafeIcon icon={FiZap} className="text-white" /></div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-white">Snippet Boss</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <SafeIcon icon={isOnline ? FiWifi : FiWifiOff} className={`text-[9px] ${!isOnline ? 'text-gray-500' : syncStatus === 'retrying' ? 'text-amber-400' : syncStatus === 'syncing' ? 'text-indigo-400' : 'text-emerald-400'}`} />
                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{!isOnline ? 'offline' : syncStatus}{dirtyIds.length ? ` · ${dirtyIds.length}` : ''}</span>
              </div>
            </div>
          </div>

          <div className="relative flex-1 min-w-0">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              ref={tabletSearchInputRef}
              type="text"
              title={`Filters: ${searchHelp.join(' · ')}`}
              placeholder="Search snippets…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="header-search w-full h-11 rounded-xl pl-10 pr-3 text-sm outline-none"
            />
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} className="top-action touch-target w-11 h-11 flex items-center justify-center rounded-xl"><SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} /></button>
            <button onClick={() => setIsInsightsOpen(true)} title="Insights" className="top-action touch-target w-11 h-11 flex items-center justify-center rounded-xl"><SafeIcon icon={FiBarChart2} /></button>
            <button onClick={() => { setIsSelectionMode(v => !v); setSelectedIds(new Set()); }} title="Select" className={`touch-target w-11 h-11 flex items-center justify-center rounded-xl border ${isSelectionMode ? 'bg-indigo-600/20 border-indigo-500 text-indigo-300' : 'bg-gray-800 border-gray-700 text-gray-400'}`}><SafeIcon icon={FiCheckSquare} /></button>
            <button onClick={() => syncNowInternal({ mode: 'manual' })} title="Sync" className="top-action touch-target w-11 h-11 flex items-center justify-center rounded-xl"><SafeIcon icon={FiRefreshCcw} className={syncStatus === 'syncing' ? 'animate-spin' : ''} /></button>
            <button onClick={() => setIsModalOpen(true)} title="New snippet" className="touch-target h-11 px-4 flex items-center justify-center gap-2 rounded-xl bg-indigo-600 text-white font-bold shadow-lg shadow-indigo-500/20"><SafeIcon icon={FiPlus} /><span>New</span></button>
            <button onClick={() => setIsSettingsOpen(true)} title="Settings" className="touch-target w-11 h-11 flex items-center justify-center rounded-xl text-gray-500 active:bg-gray-800"><SafeIcon icon={FiSettings} /></button>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-indigo-600 rounded-lg shrink-0"><SafeIcon icon={FiZap} className="text-white text-sm" /></div>
            <h1 className="text-lg font-bold theme-text tracking-tight">Snippets</h1>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} className="touch-target w-9 h-9 flex items-center justify-center theme-muted rounded-full"><SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} /></button>
            <button onClick={() => setIsInsightsOpen(true)} className="w-8 h-8 flex items-center justify-center text-gray-400 rounded-full transition-colors active:bg-gray-100"><SafeIcon icon={FiBarChart2} /></button>
            <button onClick={() => { setIsSelectionMode(v => !v); setSelectedIds(new Set()); }} className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${isSelectionMode ? 'bg-indigo-50 text-indigo-600' : 'text-gray-400 active:bg-gray-100'}`}><SafeIcon icon={FiCheckSquare} /></button>
            <button onClick={() => setIsModalOpen(true)} className="w-8 h-8 flex items-center justify-center bg-indigo-50 text-indigo-600 rounded-full transition-colors active:bg-indigo-100">
              <SafeIcon icon={FiPlus} className="text-lg" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Toggleable Search */}
      {isMobileSearchOpen && (
        <div className="md:hidden px-4 pt-4 pb-2">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              ref={searchInputRef}
              type="text" 
              placeholder="Search… try is:permanent" 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              className="header-search w-full rounded-xl pl-10 pr-4 py-3 text-sm outline-none shadow-sm" 
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <SafeIcon icon={FiX} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filters / sort / view */}
      <div className="filter-strip snippet-shell py-2.5 flex items-center gap-3">
        <div className="native-tabs flex-1 min-w-0 flex items-center overflow-x-auto no-scrollbar gap-2">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`filter-pill flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all shrink-0 ${activeSection === tab.id ? 'active' : ''}`}
            >
              <SafeIcon icon={tab.icon} className="text-sm" />
              {tab.label}
              <span className="filter-count ml-1 px-1.5 py-0.5 rounded-full text-[10px]">
                {sectionCounts[tab.id]}
              </span>
            </button>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="library-sort h-9 rounded-lg px-2.5 text-[10px] md:text-xs outline-none">
            <option value="updated">Recently updated</option>
            <option value="used">Most used</option>
            <option value="title">Title A-Z</option>
          </select>
          <div className="flex items-center">
            <button onClick={() => setViewMode('grid')} title="Grid view" className={`library-view-button w-9 h-9 flex items-center justify-center rounded-l-lg ${viewMode === 'grid' ? 'active' : ''}`}><SafeIcon icon={FiGrid} /></button>
            <button onClick={() => setViewMode('list')} title="List view" className={`library-view-button w-9 h-9 flex items-center justify-center rounded-r-lg -ml-px ${viewMode === 'list' ? 'active' : ''}`}><SafeIcon icon={FiList} /></button>
          </div>
        </div>
      </div>

      {isSelectionMode && (
        <div className="snippet-shell pt-2 flex items-center gap-2">
          <button
            onClick={() => setSelectedIds(new Set(rankedResults.map(s => s.id)))}
            className="rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-3 py-2 text-xs font-bold text-indigo-600 md:text-indigo-300"
          >
            Select visible ({rankedResults.length})
          </button>
          <button onClick={clearSelection} className="top-action rounded-lg px-3 py-2 text-xs font-bold">
            Exit selection
          </button>
        </div>
      )}

      <main className="snippet-shell py-4 md:py-5 lg:py-6">
        
        {/* Premium quick-access Favorites shelf */}
        {pinnedSnippetsRail.length > 0 && activeSection !== 'pinned' && (
          <section className="favorites-shelf mb-3 md:mb-4">
            <div className="favorites-shelf-head px-3 md:px-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <SafeIcon icon={FiStar} className="text-yellow-500 shrink-0" />
                <span className="text-xs md:text-sm font-bold theme-text">Favorites</span>
                <span className="hidden sm:inline text-[10px] md:text-xs theme-muted truncate">Quick access to your most used snippets</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="hidden sm:inline text-[10px] theme-muted">{pinnedSnippetsRail.length} items</span>
                <button onClick={() => setRailCollapsed(!railCollapsed)} className="text-[10px] font-bold theme-muted flex items-center gap-1">
                  {railCollapsed ? 'Show' : 'Show less'} <SafeIcon icon={railCollapsed ? FiChevronDown : FiChevronUp} />
                </button>
              </div>
            </div>
            {!railCollapsed && (
              <div className="favorites-items quick-access-scroll">
                {pinnedSnippetsRail.map((s, idx) => (
                  <div key={s.id} className="favorite-card px-2.5">
                    <span className="text-[10px] font-bold theme-muted shrink-0">{idx + 1}</span>
                    <button onClick={() => executeSnippetCopy(s, 'plain')} className="min-w-0 flex-1 text-left">
                      <span className="block text-[11px] md:text-xs font-semibold truncate theme-text-2">{s.title}</span>
                    </button>
                    <SafeIcon icon={FiStar} className="text-yellow-500 fill-current text-xs shrink-0" />
                    <button onClick={() => setPreviewSnippet(s)} className="snippet-icon-button w-7 h-7 flex items-center justify-center shrink-0" title="Preview"><SafeIcon icon={FiFileText} className="text-xs" /></button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <div className="library-toolbar lg:hidden mb-2.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-bold theme-text">{TABS.find(t => t.id === activeSection)?.label || 'All Snippets'}</h2>
            <p className="text-[10px] theme-muted mt-0.5">{rankedResults.length} snippet{rankedResults.length === 1 ? '' : 's'}</p>
          </div>
          <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="library-sort h-9 rounded-lg px-2.5 text-[10px] outline-none shrink-0">
            <option value="updated">Recent</option>
            <option value="used">Most used</option>
            <option value="title">A-Z</option>
          </select>
        </div>

        {/* Main Grid */}
        <div className={`snippet-grid ${viewMode === 'list' ? 'list-mode' : ''}`}>
          {rankedResults.map(s => (
            <SnippetCard 
              key={s.id} 
              snippet={s} 
              onCopy={executeSnippetCopy} 
              onDelete={() => setDeleteId(s.id)} 
              onEdit={(snip) => { setEditingSnippet(snip); setIsModalOpen(true); }} 
              onPreview={setPreviewSnippet}
              isPinned={s.isPinned} 
              onTogglePin={handleTogglePin}
              onTogglePermanent={handleTogglePermanent}
              isSelectionMode={isSelectionMode}
              isSelected={selectedIds.has(s.id)}
              onSelect={toggleSelected}
              usageCount={Math.max(Number(usageCounts[s.id] || 0), Number(s.usageCount || 0))}
              viewMode={viewMode}
            />
          ))}
          {rankedResults.length === 0 && (
            <div className="col-span-full py-24 text-center">
              <div className="theme-surface border theme-border w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiSearch} className="text-4xl text-gray-400 md:text-gray-700" />
              </div>
              <h3 className="text-xl font-bold theme-text mb-2">No snippets found</h3>
              <p className="text-gray-500 text-sm max-w-xs mx-auto">Try adjusting your filter or search query to find what you're looking for.</p>
            </div>
          )}
        </div>
      </main>

      <BulkActionBar
        count={selectedIds.size}
        onClear={clearSelection}
        onPermanent={() => applyBulkPermanent(true)}
        onTemporary={() => applyBulkPermanent(false)}
        onDelete={applyBulkDelete}
        onExport={exportSelectedSnippets}
        onPin={applyBulkPin}
        onUnpin={applyBulkUnpin}
      />

      {/* Mobile Bottom Navigation */}
      <div className="theme-surface md:hidden fixed bottom-0 left-0 right-0 backdrop-blur-xl border-t theme-border px-5 pt-2.5 pb-[max(0.65rem,env(safe-area-inset-bottom))] flex justify-between items-center z-40 shadow-[0_-6px_24px_rgba(0,0,0,0.08)]">
        <button onClick={() => { setActiveSection('all'); window.scrollTo({top: 0, behavior: 'smooth'}); }} className={`flex flex-col items-center gap-1 ${activeSection === 'all' ? 'text-indigo-600' : 'text-gray-400'}`}>
          <SafeIcon icon={FiFileText} className="text-xl" />
          <span className="text-[10px] font-medium">Snippets</span>
        </button>
        <button onClick={() => { setActiveSection('pinned'); window.scrollTo({top: 0, behavior: 'smooth'}); }} className={`flex flex-col items-center gap-1 ${activeSection === 'pinned' ? 'text-indigo-600' : 'text-gray-400'}`}>
          <SafeIcon icon={FiStar} className="text-xl" />
          <span className="text-[10px] font-medium">Pinned</span>
        </button>
        
        <div className="relative -top-5">
          <button onClick={() => setIsModalOpen(true)} className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-indigo-500/30 transition-transform active:scale-95">
            <SafeIcon icon={FiPlus} className="text-2xl" />
          </button>
        </div>
        
        <button onClick={() => {
          setIsMobileSearchOpen(!isMobileSearchOpen);
          if (!isMobileSearchOpen) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
            window.scrollTo({top: 0, behavior: 'smooth'});
          }
        }} className={`flex flex-col items-center gap-1 ${isMobileSearchOpen ? 'text-indigo-600' : 'text-gray-400'}`}>
          <SafeIcon icon={FiSearch} className="text-xl" />
          <span className="text-[10px] font-medium">Search</span>
        </button>
        <button onClick={() => setIsSettingsOpen(true)} className="flex flex-col items-center gap-1 text-gray-400">
          <SafeIcon icon={FiSettings} className="text-xl" />
          <span className="text-[10px] font-medium">More</span>
        </button>
      </div>

      <AnimatePresence>
        {isInsightsOpen && <InsightsModal snippets={snippets} usageCounts={usageCounts} onClose={() => setIsInsightsOpen(false)} />}
        {isDuplicatesOpen && <DuplicateManagerModal groups={duplicateGroups} usageCounts={usageCounts} onClose={() => setIsDuplicatesOpen(false)} onKeepBest={keepBestDuplicate} />}
        {isTrashOpen && <TrashModal snippets={snippets} onClose={() => setIsTrashOpen(false)} onRestore={restoreSnippet} />}
        {pendingImport && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }} className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-900 p-6 shadow-2xl">
              <h2 className="text-xl font-bold text-white">Import snippets</h2>
              <p className="mt-2 text-sm text-gray-400">Choose how this backup should be applied. Merge skips identical title/content pairs. Replace keeps cloud-safe deletion tombstones so removed records do not reappear on another device.</p>
              <div className="mt-6 grid gap-3">
                <button onClick={() => applyImport('merge')} className="w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white hover:bg-indigo-500">Merge with existing</button>
                <button onClick={() => applyImport('replace')} className="w-full rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm font-bold text-amber-300 hover:bg-amber-500/20">Replace local library</button>
                <button onClick={() => setPendingImport(null)} className="w-full rounded-xl border border-gray-700 bg-gray-800 px-4 py-3 text-sm font-bold text-gray-300 hover:bg-gray-700">Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {tokenModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className={`fixed inset-0 z-[200] flex bg-black/80 backdrop-blur-md ${isMobileUI ? 'items-end' : 'items-center justify-center p-4 sm:p-6'}`}
            onClick={(e) => e.target === e.currentTarget && setTokenModal(null)}
          >
            <motion.div 
              initial={isMobileUI ? { y: "100%" } : { scale: 0.95, opacity: 0, y: 20 }} 
              animate={isMobileUI ? { y: 0 } : { scale: 1, opacity: 1, y: 0 }} 
              exit={isMobileUI ? { y: "100%" } : { scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              style={isMobileUI ? {
                maxHeight: `${Math.max(320, viewportHeight - 8)}px`,
                marginBottom: `${keyboardInset}px`,
                transition: "margin-bottom 0.2s ease, max-height 0.2s ease"
              } : { maxHeight: '85vh' }}
              className={`bg-gray-900 border-gray-700 w-full shadow-2xl flex flex-col overflow-hidden ${isMobileUI ? 'rounded-t-3xl border-t' : 'border rounded-3xl max-w-lg'}`}
            >
              <div className="p-5 sm:p-6 border-b border-gray-800 flex justify-between items-center bg-gray-900/50 shrink-0 sticky top-0 z-10">
                <div className="min-w-0 pr-4">
                  <h2 className="text-lg sm:text-xl font-bold text-white truncate">Fill Variables</h2>
                  <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 truncate">{tokenModal.snippet.title}</p>
                </div>
                <button onClick={() => setTokenModal(null)} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-all shrink-0"><SafeIcon icon={FiX} /></button>
              </div>
              
              <div 
                className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5 custom-scrollbar"
                style={{
                  paddingBottom: "max(16px, env(safe-area-inset-bottom))",
                  WebkitOverflowScrolling: "touch"
                }}
              >
                {Object.keys(tokenValues).map((token, idx) => (
                  <div key={token}>
                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1">
                      {token.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </label>
                    <input 
                      autoFocus={idx === 0} 
                      type="text" 
                      className="w-full bg-gray-950 border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-gray-700" 
                      placeholder={`Enter ${token}...`} 
                      value={tokenValues[token]} 
                      onChange={(e) => setTokenValues(prev => ({ ...prev, [token]: e.target.value }))} 
                      onFocus={(e) => {
                        if (isMobileUI) {
                          setTimeout(() => {
                            e.target.scrollIntoView({ block: "center", behavior: "smooth" });
                          }, 350);
                        }
                      }}
                      required 
                    />
                  </div>
                ))}
              </div>

              <div className={`p-5 sm:p-6 border-t border-gray-800 bg-gray-900 shrink-0 sticky bottom-0 z-10 ${isMobileUI ? 'pb-[max(1.25rem,env(safe-area-inset-bottom))]' : ''}`}>
                <button 
                  onClick={() => { executeSnippetCopy(tokenModal.snippet, tokenModal.mode, tokenValues); setTokenModal(null); }}
                  className="w-full h-14 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl shadow-xl transition-all font-black text-sm tracking-widest uppercase flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiCopy} /> Copy Final Result
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {previewSnippet && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-md">
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="bg-gray-900 border border-gray-700 rounded-3xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden">
              <div className="p-6 border-b border-gray-800 flex justify-between items-center bg-gray-900/50 sticky top-0">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-white break-words pr-4">{previewSnippet.title}</h2>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-[10px] font-black px-2 py-0.5 rounded uppercase bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">{previewSnippet.type}</span>
                    <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">{previewSnippet.content.length} characters</span>
                  </div>
                </div>
                <button onClick={() => setPreviewSnippet(null)} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-all shrink-0"><SafeIcon icon={FiX} className="text-xl" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 bg-gray-950/50 custom-scrollbar">
                {previewSnippet.type === 'plain' ? (
                  <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap break-words leading-relaxed selection:bg-indigo-500/30">{previewSnippet.content}</pre>
                ) : (
                  <div className="snippet-html-preview" dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewSnippet.content) }} />
                )}
              </div>
              <div className="p-6 border-t border-gray-800 bg-gray-900 sticky bottom-0 flex gap-3">
                <button onClick={() => executeSnippetCopy(previewSnippet, 'plain')} className="flex-1 h-12 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-xl border border-gray-700 transition-all font-bold text-sm">
                  <SafeIcon icon={FiCopy} /> Copy Plain
                </button>
                {previewSnippet.type === 'html' && (
                  <>
                    <button onClick={() => executeSnippetCopy(previewSnippet, 'html')} className="flex-1 h-12 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl shadow-lg transition-all font-bold text-sm">
                      <SafeIcon icon={FiCode} /> Rich
                    </button>
                    <button onClick={() => executeSnippetCopy(previewSnippet, 'markdown')} className="flex-1 h-12 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-xl border border-gray-700 transition-all font-bold text-sm">
                      MD
                    </button>
                    <button onClick={() => executeSnippetCopy(previewSnippet, 'source')} className="flex-1 h-12 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-xl border border-gray-700 transition-all font-bold text-sm">
                      HTML
                    </button>
                  </>
                )}
                <button onClick={() => setPreviewSnippet(null)} className="sm:hidden flex items-center justify-center px-6 h-12 bg-gray-850 hover:bg-gray-800 text-gray-400 rounded-xl border border-gray-800 transition-all font-bold text-sm">Close</button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {pendingClipboard && <ClipboardCaptureUI data={pendingClipboard} deviceClass={deviceClass} onDismiss={() => setPendingClipboard(null)} onCreate={() => { createSnippetFromData(pendingClipboard, pendingClipboard.hash); setPendingClipboard(null); }} />}
        {isSettingsOpen && <SettingsModal onClose={() => setIsSettingsOpen(false)} config={config} setConfig={setConfig} appsScriptUrl={appsScriptUrl} setAppsScriptUrl={setAppsScriptUrl} onSyncNow={syncNowInternal} syncStatus={syncStatus} dirtyCount={dirtyIds.length} lastSyncAt={lastSyncAt} isOnline={isOnline} retryCount={retryCount} lastSyncError={lastSyncError} blockedCount={cloudBlockedSnippets.length} onExport={exportAllSnippets} onImport={() => importInputRef.current?.click()} onInsights={() => { setIsSettingsOpen(false); setIsInsightsOpen(true); }} onDuplicates={() => { setIsSettingsOpen(false); setIsDuplicatesOpen(true); }} onTrash={() => { setIsSettingsOpen(false); setIsTrashOpen(true); }} theme={theme} onToggleTheme={toggleTheme} />}
        {isModalOpen && <SnippetModal onClose={() => { setIsModalOpen(false); setEditingSnippet(null); }} onSave={(data) => {
          const now = Date.now();
          const current = snippetsRef.current;
          let updated;
          if (editingSnippet) {
            updated = current.map(s => s.id === editingSnippet.id ? { ...s, ...data, updatedAt: getNextUpdatedAt(s) } : s);
          } else {
            updated = [{
              ...data,
              id: generateId(),
              createdAt: now,
              updatedAt: now,
              lastUsedAt: now,
              usageCount: 0,
              isDeleted: false,
              isPinned: false,
              isPermanent: Boolean(data.isPermanent)
            }, ...current];
          }
          const changedId = editingSnippet?.id || updated[0]?.id;
          commitLocalMutation(updated, changedId);
          setIsModalOpen(false);
          setEditingSnippet(null);
        }} editingSnippet={editingSnippet} />}
        {deleteId && <DeleteConfirmModal onClose={() => setDeleteId(null)} onConfirm={() => {
          const updated = snippetsRef.current.map(s => s.id === deleteId ? { ...s, isDeleted: true, updatedAt: getNextUpdatedAt(s) } : s);
          commitLocalMutation(updated, deleteId);
          setDeleteId(null);
        }} title={snippets.find(s => s.id === deleteId)?.title} />}
        {toast && <Toast message={toast.message} type={toast.type} action={toast.action} onActionClick={() => { if (toast.action?.onClick) toast.action.onClick(); setToast(null); }} />}
        
        <CommandPalette
          isOpen={isCommandPaletteOpen}
          onClose={() => setIsCommandPaletteOpen(false)}
          results={commandResults}
          resetNonce={paletteResetNonce}
        />
      </AnimatePresence>
    </div>
  );
}